"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Effect } from "effect"
import { RpcClient } from "@effect/rpc"
import { useIsFeatureEnabled } from "@/entities/system-configuration"
import { chatEq, ChatRpc } from "./api/chat-client"
import { chatKeys } from "./keys"
import { useChatScope } from "./use-chat-scope"
import type { ConversationId } from "@/contexts/chat/domain"

/**
 * Unread signal for the nav bell badge. Refetches on window focus + every 2 min.
 * Gated on the chat_messaging flag: the hook is mounted from the sidebar/navbar
 * on EVERY page (React hooks can't be conditional at the call site), so without
 * the `enabled` gate flag-off orgs poll getChatUnread every 2 minutes per tab
 * for a badge they can never see (chat-cost investigation 2026-07-16).
 *
 * `chat_messaging` is a broker (core) rollout flag. A non-core product's shell
 * passes `consultRolloutFlag: false` (customer-success K5: rollout flags are
 * not consulted for non-core products), so its badge polls regardless. The
 * default keeps the broker gate unchanged.
 */
export interface UseChatUnreadOptions {
  /** Gate the read on the `chat_messaging` rollout flag. Default `true`. */
  readonly consultRolloutFlag?: boolean
}

export function useChatUnread({ consultRolloutFlag = true }: UseChatUnreadOptions = {}) {
  const scope = useChatScope()
  const isChatEnabled = useIsFeatureEnabled("chat_messaging")
  return useQuery({
    ...chatEq.queryOptions({
      queryKey: [...chatKeys.unread, scope],
      queryFn: () =>
        Effect.gen(function* () {
          const rpc = yield* RpcClient.make(ChatRpc)
          return yield* rpc.getChatUnread()
        }).pipe(Effect.scoped),
      staleTime: 60 * 1000,
    }),
    enabled: !consultRolloutFlag || isChatEnabled,
    // No interval: the realtime bridge invalidates the unread key on every
    // pushed message, so the badge updates on the event rather than on a timer
    // that ran in every tab of every signed-in user.
    refetchOnWindowFocus: true,
  })
}

/** Mark a conversation read (clears its unread flag). Pass the conversation id. */
export function useMarkChatRead() {
  const qc = useQueryClient()
  return useMutation({
    ...chatEq.mutationOptions({
      mutationKey: ["chat", "mark-read"],
      mutationFn: (conversationId: string) =>
        Effect.gen(function* () {
          const rpc = yield* RpcClient.make(ChatRpc)
          return yield* rpc.markRead({ conversationId: conversationId as ConversationId })
        }).pipe(Effect.scoped),
      onSuccess: (_data, conversationId) => {
        qc.invalidateQueries({ queryKey: chatKeys.unread })
        qc.invalidateQueries({ queryKey: chatKeys.messages(conversationId) })
        // Also refresh the inbox list so the row's unread dot clears immediately.
        qc.invalidateQueries({ queryKey: chatKeys.conversations })
      },
    }),
  })
}
